import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { OvertimeReasonsTable, AddEditReasonModal } from "./index";
import { useOvertimeReasons } from "./hooks/useOvertimeReasons";
import { OvertimeReason } from "@/types/supabase-types";
import { toast } from "sonner";
import { useAuth } from "@/contexts/auth";
import { UserRole } from "@/types/enums";
import { Clock, Plus } from "lucide-react";

export function OvertimeReasonsSettings() {
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedReason, setSelectedReason] = useState<OvertimeReason | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const { user } = useAuth();
  const {
    overtimeReasons,
    isLoading,
    error,
    addReason,
    updateReason
    // deleteReason
  } = useOvertimeReasons();
  
  const openEditDialog = (reason: OvertimeReason) => {
    setSelectedReason(reason);
    setIsEditDialogOpen(true);
  };

  const handleAddReason = async (data: { main_title: string; reason: string; code?: string }) => {
    setIsSubmitting(true);
    try {
      const success = await addReason(data);
      if (success) {
        toast.success("Overtime reason added successfully");
        setIsAddDialogOpen(false);
        return true;
      } else {
        toast.error("Failed to add overtime reason");
        return false;
      }
    } catch {
      toast.error("An unexpected error occurred while adding the reason");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateReason = async (data: { id: string; main_title: string; reason: string; code?: string }) => {
    setIsSubmitting(true);
    try {
      const success = await updateReason(data);
      if (success) {
        toast.success("Overtime reason updated successfully");
        setIsEditDialogOpen(false);
        return true;
      } else {
        toast.error("Failed to update overtime reason");
        return false;
      }
    } catch {
      toast.error("An unexpected error occurred while updating the reason");
    } finally {
      setIsSubmitting(false);
    }
  };

  const canManageReasons = user?.role === UserRole.HR || user?.role === UserRole.SUPER_ADMIN;

  if (error) {
    return (
      <Card className="shadow-sm border-border/50">
        <CardContent className="pt-6">
          <div className="text-center py-8">
            <p className="text-destructive">Error loading overtime reasons: {error}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="shadow-sm border-border/50">
      <CardHeader className="pb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <Clock className="w-5 h-5 text-primary" />
            </div>
            <div>
              <CardTitle className="text-2xl font-semibold text-foreground">Overtime Reasons Management</CardTitle>
            </div>
          </div>
          {canManageReasons && (
            <Button 
              onClick={() => setIsAddDialogOpen(true)}
              size="default"
              variant="secondary"
              className="px-4 h-10"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Reason
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
            <span className="ml-3 text-muted-foreground font-medium">Loading overtime reasons...</span>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between text-sm text-muted-foreground mb-4">
              <span>Total Reasons: {overtimeReasons.length}</span>
            </div>
            <OvertimeReasonsTable 
              overtimeReasons={overtimeReasons} 
              onEdit={openEditDialog} 
            />
          </div>
        )}
      </CardContent>
      
      <AddEditReasonModal
        open={isAddDialogOpen}
        onOpenChange={setIsAddDialogOpen}
        onSubmit={handleAddReason}
        isSubmitting={isSubmitting}
        mode="add"
      />
      
      <AddEditReasonModal
        open={isEditDialogOpen}
        onOpenChange={setIsEditDialogOpen}
        onSubmit={handleUpdateReason}
        isSubmitting={isSubmitting}
        mode="edit"
        reason={selectedReason}
      />
    </Card>
  );
}
